import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Table } from 'reactstrap';
import { getCurrencyCode } from '../Base/Common/ReferenceDataFunctions';

// Configuration fields of a channel instance, shared by the save warning and the change history
export const channelInstanceFields = [
    { key: 'name', labelId: 'ChannelList.ChannelName', type: 'text' },
    { key: 'user', labelId: 'ChannelList.User', type: 'text' },
    { key: 'password', labelId: 'ChannelList.Password', type: 'credential' },
    { key: 'secondaryUser', labelId: 'ChannelConfigState.SecondaryUser', type: 'text' },
    { key: 'secondaryPassword', labelId: 'ChannelConfigState.SecondaryPassword', type: 'credential' },
    { key: 'propertyCode', labelId: 'ChannelList.propertyCode', type: 'text' },
    { key: 'maxInventoryDays', labelId: 'ChannelList.maxInventoryDays', type: 'number' },
    { key: 'sendAvailability', labelId: 'ChannelList.SendAvailability', groupId: 'ChannelList.InformationSentToChannel', type: 'bool' },
    { key: 'sendPrice', labelId: 'ChannelList.SendPrice', groupId: 'ChannelList.InformationSentToChannel', type: 'bool' },
    { key: 'sendCta', labelId: 'ChannelList.SendCta', groupId: 'ChannelList.InformationSentToChannel', type: 'bool' },
    { key: 'sendCtd', labelId: 'ChannelList.SendCtd', groupId: 'ChannelList.InformationSentToChannel', type: 'bool' },
    { key: 'sendMaxStay', labelId: 'ChannelList.SendMaxStay', groupId: 'ChannelList.InformationSentToChannel', type: 'bool' },
    { key: 'sendMinStay', labelId: 'ChannelList.SendMinStay', groupId: 'ChannelList.InformationSentToChannel', type: 'bool' },
    { key: 'sendStopSales', labelId: 'ChannelList.SendStopSales', groupId: 'ChannelList.InformationSentToChannel', type: 'bool' },
    { key: 'sendMinAdvancedBookingOffset', labelId: 'ChannelList.SendMinAdvancedBookingOffset', groupId: 'ChannelList.InformationSentToChannel', type: 'bool' },
    { key: 'sendMaxAdvancedBookingOffset', labelId: 'ChannelList.SendMaxAdvancedBookingOffset', groupId: 'ChannelList.InformationSentToChannel', type: 'bool' },
    { key: 'sendOverbookingAvailability', labelId: 'ChannelList.SendOverbookingAvailability', groupId: 'ChannelList.InformationSentToChannel', type: 'bool' },
    { key: 'convertCurrencyFromReservation', labelId: 'ChannelList.ConvertCurrencyFromReservation', groupId: 'ChannelList.CurrencyConfig', type: 'bool' },
    { key: 'convertCurrencyToChannel', labelId: 'ChannelList.ConvertCurrencyToChannel', groupId: 'ChannelList.CurrencyConfig', type: 'bool' },
    { key: 'currencyConvertion', labelId: 'ChannelList.Currency', groupId: 'ChannelList.CurrencyConfig', type: 'currency' },
    { key: 'applyRoomsDifferenceAsDiscount', labelId: 'ChannelList.DifferencesAsDiscounts', groupId: 'ChannelList.DiscountsConfig', type: 'bool' },
    { key: 'channelConfigState', labelId: 'HotelChannelInfoDetail.ChannelConfigState', type: 'state' }
];

// Mirrors the deactivation rule of CreateUpdateChannelInstance (Rate, ChannelConfigController):
// the API sets Active = false when any of these differs from the stored value, a missing field included
const deactivatingFields = ['propertyCode', 'user', 'secondaryUser', 'secondaryPassword', 'sendAvailability', 'sendPrice', 'sendStopSales', 'sendCta', 'sendCtd', 'sendMinStay', 'sendMaxStay'];

export const normalize = (value, type) => {
    if (type === 'bool') {
        return value === true;
    }
    if (value === null || value === undefined || value === '' || Number.isNaN(value)) {
        return '';
    }
    return String(value);
};

export const formatValue = (value, type, intl) => {
    if (type === 'bool') {
        return intl.formatMessage({ id: value ? 'generic.yes' : 'generic.no' });
    }
    if (value === '') {
        return intl.formatMessage({ id: 'ChannelInstanceChanges.Empty' });
    }
    if (type === 'currency') {
        const currency = getCurrencyCode().find(c => c.value === value);
        return currency ? currency.label : value;
    }
    if (type === 'state') {
        return intl.formatMessage({ id: `ChannelConfigState.${value.replace(/ /g, '')}`, defaultMessage: value });
    }
    return value;
};

// labelIds overrides a field label, e.g. the password is an API key on Email Reservation
export const getChannelInstanceChanges = (original, current, intl, fieldKeys, labelIds = {}) => {
    const fields = fieldKeys ? channelInstanceFields.filter(f => fieldKeys.includes(f.key)) : channelInstanceFields;

    return fields.reduce((changes, field) => {
        const from = normalize(original?.[field.key], field.type);
        const to = normalize(current?.[field.key], field.type);

        if (from !== to) {
            const label = intl.formatMessage({ id: labelIds[field.key] || field.labelId });

            changes.push({
                key: field.key,
                label: field.groupId ? `${intl.formatMessage({ id: field.groupId })}: ${label}` : label,
                credential: field.type === 'credential',
                from: field.type === 'credential' ? null : formatValue(from, field.type, intl),
                to: field.type === 'credential' ? null : formatValue(to, field.type, intl)
            });
        }

        return changes;
    }, []);
};

export const willDeactivateChannel = (original, payload) =>
    deactivatingFields.some(key => (payload?.[key] ?? null) !== (original?.[key] ?? null));

// An active channel instance asks for confirmation when something changes or when the API is going to deactivate it.
// Returns what the warning shows, or null when the save goes ahead without one
export const getSaveWarning = (saved, payload, intl, fieldKeys, labelIds) => {
    if (!saved?.active) {
        return null;
    }

    const changes = getChannelInstanceChanges(saved, payload, intl, fieldKeys, labelIds);
    const willDeactivate = willDeactivateChannel(saved, payload);

    return changes.length > 0 || willDeactivate ? { changes, willDeactivate } : null;
};

export const ChannelInstanceChangesWarning = ({ channelName, changes, willDeactivate }) => (
    <div className="text-left">
        <p>
            <FormattedMessage id={changes.length > 0 ? 'ChannelInstanceChanges.ActiveWarning' : 'ChannelInstanceChanges.ActiveNoChanges'} values={{ channel: <strong>{channelName}</strong> }} />
        </p>
        <p>
            <FormattedMessage id="ChannelInstanceChanges.ConfirmSave" />
        </p>
        {changes.length > 0 ?
            <Table size="sm" className="mb-3">
                <thead>
                    <tr>
                        <th><FormattedMessage id="ChannelInstanceChanges.Field" /></th>
                        <th><FormattedMessage id="ChannelInstanceChanges.CurrentValue" /></th>
                        <th><FormattedMessage id="ChannelInstanceChanges.NewValue" /></th>
                    </tr>
                </thead>
                <tbody>
                    {changes.map(change => (
                        <tr key={change.key}>
                            <td>{change.label}</td>
                            {change.credential ?
                                <td colSpan="2" className="text-muted"><FormattedMessage id="ChannelInstanceChanges.CredentialChanged" /></td>
                                :
                                <>
                                    <td>{change.from}</td>
                                    <td>{change.to}</td>
                                </>
                            }
                        </tr>
                    ))}
                </tbody>
            </Table>
            : ''}
        {willDeactivate ?
            <p className="text-danger mb-0">
                <i className="fas fa-exclamation-triangle mr-2" />
                <FormattedMessage id="ChannelInstanceChanges.WillDeactivate" />
            </p>
            : ''}
    </div>
);
